import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { AdminGrowthResponse, MonthlyGrowth } from '@avanzu/types';
import { apiClient } from '../services/apiClient';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export default function GrowthChart() {
  const theme = useTheme();
  const [growth, setGrowth] = useState<MonthlyGrowth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGrowth();
  }, []);

  const fetchGrowth = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/admin/growth');

      if (!response.ok) {
        throw new Error('Failed to fetch growth data');
      }

      const data: AdminGrowthResponse = await response.json();
      setGrowth(data.growth);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching growth data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (month: string): string => {
    const [year, monthNum] = month.split('-');
    const date = new Date(parseInt(year), parseInt(monthNum) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const chartData = growth.map((item) => ({
    month: formatMonth(item.month),
    'New Users': item.userCount,
    'Active Users': item.activeUsers || 0,
    Organisations: item.organisationCount,
  }));

  return (
    <Card>
      <CardHeader title="Growth Over Time" />
      <CardContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : growth.length === 0 ? (
          <Typography color="text.secondary">No growth data available</Typography>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="month"
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '12px' }}
              />
              <YAxis stroke={theme.palette.text.secondary} style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="New Users"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={{ fill: theme.palette.primary.main, r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="Active Users"
                stroke={theme.palette.info.main}
                strokeWidth={2}
                dot={{ fill: theme.palette.info.main, r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="Organisations"
                stroke={theme.palette.success.main}
                strokeWidth={2}
                dot={{ fill: theme.palette.success.main, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
